import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Building2,
  RefreshCw,
  Layers,
  TrendingUp,
  ChevronRight,
  Receipt,
  ShoppingCart,
  BarChart3,
  UserPlus,
  Upload,
  FileText,
  Check,
  MapPin,
  Package,
  Clock,
  Home as HomeIcon,
  BookOpen,
} from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

async function safeGet(url, config, fallback = null) {
  try {
    const res = await axios.get(url, config);
    return res.data;
  } catch {
    return fallback;
  }
}

function formatEuro(n) {
  const v = Number(n) || 0;
  return `€ ${v.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatPercShare(part, total) {
  if (!total || total <= 0) return "—";
  return `${Math.round((part / total) * 100)}% del fatturato`;
}

function formatActivityWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "";
  }
}

/** Serie giornaliere aggregate (stesso mese per tutte le strutture dall'API). */
function buildAggregatedDailyCharts(appliedRows) {
  let ndays = 0;
  for (const r of appliedRows) {
    const s = r.serie_mese_corrente;
    if (!s) continue;
    const g = Number(s.giorni) || 0;
    const rv = s.ricavi_per_giorno;
    const sp = s.spese_per_giorno;
    ndays = Math.max(ndays, g, Array.isArray(rv) ? rv.length : 0, Array.isArray(sp) ? sp.length : 0);
  }
  if (!ndays) {
    const now = new Date();
    ndays = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  }
  const ricavi = Array(ndays).fill(0);
  const spese = Array(ndays).fill(0);
  for (const r of appliedRows) {
    const s = r.serie_mese_corrente;
    const rv = s?.ricavi_per_giorno;
    const sp = s?.spese_per_giorno;
    if (Array.isArray(rv)) {
      for (let i = 0; i < Math.min(ndays, rv.length); i++) ricavi[i] += Number(rv[i]) || 0;
    }
    if (Array.isArray(sp)) {
      for (let i = 0; i < Math.min(ndays, sp.length); i++) spese[i] += Number(sp[i]) || 0;
    }
  }
  const margine = ricavi.map((x, i) => x - spese[i]);
  const toChart = (arr) => arr.map((v, i) => ({ giorno: i + 1, v }));
  return {
    ricavi: toChart(ricavi),
    spese: toChart(spese),
    margine: toChart(margine),
  };
}

function MonthlySparkline({ data, stroke, gradientId }) {
  if (!data?.length) return null;
  const hasAny = data.some((d) => Number(d.v) !== 0);
  return (
    <div className="mt-3 h-[52px] w-full shrink-0">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={0.24} />
              <stop offset="100%" stopColor={stroke} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="giorno" hide />
          <YAxis hide domain={hasAny ? ["auto", "auto"] : [0, 1]} />
          <Area
            type="monotone"
            dataKey="v"
            stroke={stroke}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={{ r: 2.2, fill: stroke, strokeWidth: 0 }}
            activeDot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function DashboardAggregatePanel({ token, dashboardFatturazione }) {
  const cfg = useMemo(
    () => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} }),
    [token],
  );

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rows, setRows] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [draftIds, setDraftIds] = useState(() => new Set());
  const [appliedIds, setAppliedIds] = useState(() => new Set());
  const [recentActivities, setRecentActivities] = useState([]);

  const load = useCallback(async () => {
    const [data, attData] = await Promise.all([
      safeGet(`${API_URL}/api/dashboard/metrics-strutture`, cfg, null),
      safeGet(`${API_URL}/api/dashboard/attivita-recenti-strutture`, cfg, { items: [] }),
    ]);
    if (!data?.multi_struttura || !Array.isArray(data.strutture)) {
      setRows([]);
      setGeneratedAt(null);
      setDraftIds(new Set());
      setAppliedIds(new Set());
      setRecentActivities([]);
      return;
    }
    setRows(data.strutture);
    setGeneratedAt(data.generated_at || null);
    const all = new Set(data.strutture.map((s) => s.id));
    setDraftIds(all);
    setAppliedIds(all);
    setRecentActivities(Array.isArray(attData?.items) ? attData.items : []);
  }, [cfg]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await load();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const toggleDraft = (id, checked) => {
    setDraftIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const applySelection = () => {
    setAppliedIds(new Set(draftIds));
  };

  const appliedRows = useMemo(
    () => rows.filter((r) => appliedIds.has(r.id)),
    [rows, appliedIds]
  );

  const aggregates = useMemo(() => {
    let postiT = 0;
    let occ = 0;
    let liberi = 0;
    let ricavi = 0;
    let spese = 0;
    let ospitiAttivi = 0;
    let ospitiResidenti = 0;
    for (const r of appliedRows) {
      postiT += r.posti_totali || 0;
      occ += r.posti_occupati || 0;
      liberi += r.posti_disponibili || 0;
      ricavi += r.ricavi_mese || 0;
      spese += r.spese_mese || 0;
      ospitiAttivi += r.ospiti_attivi || 0;
      ospitiResidenti += r.ospiti_residenti || 0;
    }
    const margine = ricavi - spese;
    const occPerc = postiT > 0 ? Math.round((occ / postiT) * 100) : 0;
    const residentiPerc = ospitiAttivi > 0 ? Math.round((ospitiResidenti / ospitiAttivi) * 100) : 0;
    return {
      postiT,
      occ,
      liberi,
      ricavi,
      spese,
      margine,
      occPerc,
      ospitiAttivi,
      ospitiResidenti,
      residentiPerc,
    };
  }, [appliedRows]);

  const diarioOggiAggregato = useMemo(() => {
    let consegne = 0;
    let segnalazioni = 0;
    for (const r of appliedRows) {
      consegne += Number(r.diario_consegne_oggi) || 0;
      segnalazioni += Number(r.diario_segnalazioni_oggi) || 0;
    }
    return { consegne, segnalazioni };
  }, [appliedRows]);

  const dailyCharts = useMemo(() => buildAggregatedDailyCharts(appliedRows), [appliedRows]);

  const deblisteringApplied = useMemo(() => {
    const rows = appliedRows.map((r) => ({
      id: r.id,
      name: r.name,
      deb: r.deblistering || {},
      posti_totali: r.posti_totali || 0,
    }));
    let totAtt = 0;
    let totPend = 0;
    let totRicavo = 0;
    for (const x of rows) {
      const d = x.deb;
      totAtt += Number(d.ospiti_attivi) || 0;
      totPend += (Number(d.fatture_da_assegnare) || 0) + (Number(d.fatture_da_inserire) || 0);
      totRicavo += Number(d.ricavo_mensile_stimato) || 0;
    }
    return { rows, totAtt, totPend, totRicavo: Math.round(totRicavo * 100) / 100 };
  }, [appliedRows]);

  const activitiesFiltered = useMemo(
    () => (recentActivities || []).filter((a) => appliedIds.has(a.struttura_id)),
    [recentActivities, appliedIds]
  );

  const occupazioneData = useMemo(
    () => [
      { name: "Occupati", value: aggregates.occ, color: "#10B981" },
      { name: "Liberi", value: aggregates.liberi, color: "#94A3B8" },
      { name: "Manutenzione", value: 0, color: "#F59E0B" },
      { name: "Non disponibili", value: 0, color: "#EF4444" },
    ],
    [aggregates.occ, aggregates.liberi]
  );
  const occupazioneChartData = occupazioneData.some((d) => d.value > 0)
    ? occupazioneData
    : [{ name: "Nessun dato", value: 1, color: "#E2E8F0" }];

  const azioniRapide = [
    { label: "Nuovo ospite", icon: UserPlus, color: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-100", to: "/fascicoli-assistenziali" },
    { label: "Registra spesa", icon: ShoppingCart, color: "text-orange-600", bg: "bg-orange-50", ring: "ring-orange-100", to: "/prima-nota-ospiti" },
    { label: "Nuova fattura", icon: Receipt, color: "text-purple-600", bg: "bg-purple-50", ring: "ring-purple-100", to: dashboardFatturazione ? "/fatturazione" : "/prima-nota-ospiti" },
    { label: "Carica documento", icon: Upload, color: "text-sky-600", bg: "bg-sky-50", ring: "ring-sky-100", to: "/fascicoli-assistenziali" },
    { label: "Diario consegne", icon: BookOpen, color: "text-blue-600", bg: "bg-blue-50", ring: "ring-blue-100", to: "/diario-consegne" },
    { label: "Documenti", icon: FileText, color: "text-amber-600", bg: "bg-amber-50", ring: "ring-amber-100", to: "/documenti" },
    { label: "Risparmio", icon: BarChart3, color: "text-teal-600", bg: "bg-teal-50", ring: "ring-teal-100", to: "/risparmio" },
  ];

  const generatedLabel = generatedAt
    ? new Date(generatedAt).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })
    : "—";

  const selectionDirty = useMemo(() => {
    if (draftIds.size !== appliedIds.size) return true;
    for (const id of draftIds) {
      if (!appliedIds.has(id)) return true;
    }
    for (const id of appliedIds) {
      if (!draftIds.has(id)) return true;
    }
    return false;
  }, [draftIds, appliedIds]);

  if (loading) {
    return (
      <div className="space-y-4 md:space-y-5 animate-pulse">
        <div className="h-80 bg-slate-200 rounded-2xl" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-28 bg-slate-200 rounded-xl" />
          ))}
        </div>
        <div className="h-64 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <Card className="card-base border border-slate-200/70 shadow-sm">
        <CardContent className="p-8 text-center text-sm text-slate-500">
          Nessun dato multi-struttura disponibile.
        </CardContent>
      </Card>
    );
  }

  const allDraftSelected = rows.length > 0 && rows.every((r) => draftIds.has(r.id));

  return (
    <div className="space-y-4 md:space-y-5">
      {/* Selettore strutture + stato vista */}
      <Card className="card-base overflow-hidden border border-slate-200/80 shadow-md bg-white">
        <div className="bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-600 px-5 py-4 sm:px-6 sm:py-5 text-white">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-3 min-w-0">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25 backdrop-blur-sm">
                <Layers className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-bold tracking-tight sm:text-lg">Strutture nell&apos;aggregazione</h3>
                <p className="mt-1 text-sm text-emerald-50/95">
                  <span className="font-semibold text-white">{appliedIds.size}</span> incluse nei totali ·{" "}
                  <span className="opacity-90">
                    tocca le schede per preparare la selezione, poi conferma con{" "}
                    <span className="font-semibold text-white">Applica</span>.
                  </span>
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 lg:justify-end lg:pt-0.5">
              <span className="hidden sm:inline rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium text-emerald-50 ring-1 ring-white/20">
                Vista aggregata attiva
              </span>
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing}
                className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-white/25 backdrop-blur-sm transition hover:bg-white/25 disabled:opacity-60"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
                Aggiorna dati
              </button>
              <span className="text-[11px] text-emerald-100/90 tabular-nums">{generatedLabel}</span>
            </div>
          </div>
        </div>

        <CardContent className="space-y-5 p-5 sm:p-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {rows.map((r) => {
              const selected = draftIds.has(r.id);
              const applied = appliedIds.has(r.id);
              const loc = [r.comune, r.provincia].filter(Boolean).join(" · ");
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => toggleDraft(r.id, !selected)}
                  className={`group relative flex w-full flex-col rounded-2xl border-2 p-4 text-left transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 ${
                    selected
                      ? "border-emerald-500 bg-gradient-to-br from-emerald-50/90 via-white to-teal-50/40 shadow-md shadow-emerald-900/5 ring-1 ring-emerald-500/15"
                      : "border-slate-200/90 bg-white hover:border-slate-300 hover:bg-slate-50/90 hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors ${
                        selected ? "bg-emerald-600 text-white shadow-sm" : "bg-slate-100 text-slate-600 group-hover:bg-slate-200"
                      }`}
                    >
                      <Building2 className="h-5 w-5" strokeWidth={2} />
                    </div>
                    <div className="min-w-0 flex-1 pt-0.5">
                      <p className="font-semibold leading-snug text-slate-900 line-clamp-2">{r.name}</p>
                      {loc ? (
                        <p className="mt-1 flex items-start gap-1 text-xs text-slate-500">
                          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                          <span className="line-clamp-2">{loc}</span>
                        </p>
                      ) : null}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ${
                            selected ? "bg-white/90 text-emerald-800 ring-emerald-200" : "bg-slate-100 text-slate-600 ring-slate-200"
                          }`}
                        >
                          {r.occupazione_perc}% occ.
                        </span>
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200/80">
                          {formatEuro(r.ricavi_mese)}
                        </span>
                        {applied && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-600/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-600/20">
                            <Check className="h-3 w-3" strokeWidth={3} />
                            Nel totale
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                        selected ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-300 bg-white text-transparent"
                      }`}
                      aria-hidden
                    >
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span>
                <span className="font-semibold text-slate-800">{draftIds.size}</span> selezionate in bozza
              </span>
              {selectionDirty ? (
                <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800 ring-1 ring-amber-200">
                  Modifiche non applicate
                </span>
              ) : (
                <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800 ring-1 ring-emerald-100">
                  Selezione allineata ai grafici
                </span>
              )}
              {!allDraftSelected && draftIds.size > 0 ? (
                <span className="text-[11px] text-slate-400">Puoi confrontare un sottoinsieme di strutture.</span>
              ) : null}
            </div>
            <Button
              type="button"
              className="h-11 rounded-xl bg-emerald-600 px-8 text-sm font-semibold shadow-md shadow-emerald-900/15 hover:bg-emerald-700 sm:min-w-[140px]"
              onClick={applySelection}
              disabled={draftIds.size === 0}
            >
              Applica selezione
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPI aggregati */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
        <Card className="card-base bg-white border border-slate-200/70 shadow-sm lg:col-span-1">
          <CardContent className="p-4">
            <p className="text-[11px] font-medium text-slate-500 mb-2">Occupazione totale</p>
            <div className="flex items-center gap-3">
              <div className="relative w-28 h-28 flex-shrink-0 mx-auto sm:mx-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={occupazioneChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={44}
                      outerRadius={52}
                      paddingAngle={occupazioneChartData.length > 1 ? 4 : 0}
                      cornerRadius={8}
                      dataKey="value"
                      stroke="none"
                    >
                      {occupazioneChartData.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-xl font-bold text-emerald-600 leading-none">{aggregates.occPerc}%</span>
                  <span className="text-[10px] text-slate-500 font-medium mt-0.5">Occupazione</span>
                </div>
              </div>
              <div className="flex-1 space-y-1 min-w-0 hidden sm:block">
                {occupazioneData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-[11px]">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                      <span className="text-slate-600 truncate">{item.name}</span>
                    </div>
                    <span className="text-slate-900 font-semibold">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-2 pt-2 border-t border-slate-100">
              {aggregates.occ} occupati / {aggregates.postiT} posti letto
            </p>
          </CardContent>
        </Card>

        <Card className="card-base bg-white border border-slate-200/70 shadow-sm">
          <CardContent className="p-4 flex flex-col">
            <div className="flex items-start gap-2">
              <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 text-sky-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-slate-500">Ricavi mese</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">{formatEuro(aggregates.ricavi)}</p>
                <p className="text-[11px] text-slate-400 mt-1">Somma rette ospiti (mese corrente)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base bg-white border border-slate-200/70 shadow-sm">
          <CardContent className="p-4 flex flex-col">
            <div className="flex items-start gap-2">
              <div className="w-9 h-9 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <ShoppingCart className="w-4 h-4 text-rose-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-slate-500">Spese mese</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">{formatEuro(aggregates.spese)}</p>
                <p className="text-[11px] text-slate-400 mt-1">Prima nota e passive (mese corrente)</p>
              </div>
            </div>
            <MonthlySparkline data={dailyCharts.spese} stroke="#dc2626" gradientId="agg-spark-spese" />
          </CardContent>
        </Card>

        <Card className="card-base bg-white border border-slate-200/70 shadow-sm">
          <CardContent className="p-4 flex flex-col">
            <div className="flex items-start gap-2">
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Receipt className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-slate-500">Margine operativo</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">{formatEuro(aggregates.margine)}</p>
                <p
                  className={`text-[11px] mt-1 font-medium ${
                    aggregates.margine >= 0 ? "text-emerald-600" : "text-rose-600"
                  }`}
                >
                  {formatPercShare(aggregates.margine, aggregates.ricavi)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base bg-white border border-slate-200/70 shadow-sm">
          <CardContent className="p-4 flex flex-col h-full">
            <div className="flex items-start gap-2">
              <div className="w-9 h-9 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                <HomeIcon className="w-4 h-4 text-violet-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-slate-500">Residenti in struttura</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5 tabular-nums">
                  {aggregates.ospitiResidenti}
                </p>
                <p className="text-[11px] text-slate-400 mt-1">
                  su {aggregates.ospitiAttivi}{" "}
                  {aggregates.ospitiAttivi === 1 ? "ospite attivo" : "ospiti attivi"}
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-violet-500 rounded-full"
                  style={{ width: `${Math.min(aggregates.residentiPerc, 100)}%` }}
                />
              </div>
              <span className="text-[11px] font-bold text-violet-700 tabular-nums w-9 text-right">
                {aggregates.residentiPerc}%
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabella confronto */}
      <Card className="card-base bg-white border border-slate-200/70 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-slate-600" />
            <h3 className="text-base font-bold text-slate-900">Distribuzione per struttura</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="px-5 py-3">Struttura</th>
                  <th className="px-4 py-3 whitespace-nowrap">Occupazione</th>
                  <th className="px-4 py-3 whitespace-nowrap">Occupati / Posti</th>
                  <th className="px-4 py-3 whitespace-nowrap">Residenti</th>
                  <th className="px-4 py-3 whitespace-nowrap">Ricavi mese</th>
                  <th className="px-4 py-3 whitespace-nowrap">Spese mese</th>
                  <th className="px-5 py-3 whitespace-nowrap">Margine</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const barColor =
                    r.occupazione_perc >= 85 ? "bg-amber-500" : r.occupazione_perc >= 70 ? "bg-emerald-500" : "bg-sky-500";
                  const muted = !appliedIds.has(r.id);
                  return (
                    <tr key={r.id} className={muted ? "bg-slate-50/60 text-slate-500" : ""}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2 min-w-[180px]">
                          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-4 h-4 text-slate-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-800 truncate">{r.name}</p>
                            <p className="text-[11px] text-slate-500 truncate">
                              {[r.comune, r.provincia].filter(Boolean).join(" · ") || "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 min-w-[140px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden min-w-[72px]">
                            <div
                              className={`h-full rounded-full ${barColor}`}
                              style={{ width: `${Math.min(r.occupazione_perc || 0, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs font-bold text-slate-700 w-10 text-right">{r.occupazione_perc}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-slate-700">
                        {r.posti_occupati} / {r.posti_totali}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-slate-700">
                        <span className="tabular-nums">{r.ospiti_residenti || 0}</span>
                        <span className="text-[11px] text-slate-400 ml-1">
                          / {r.ospiti_attivi || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium">{formatEuro(r.ricavi_mese)}</td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium">{formatEuro(r.spese_mese)}</td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <span className="font-semibold text-slate-800">{formatEuro(r.margine)}</span>
                        <span className="text-[11px] text-slate-500 ml-1">
                          ({formatPercShare(r.margine, r.ricavi_mese)})
                        </span>
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-emerald-50/80 font-semibold">
                  <td className="px-5 py-3 text-emerald-900">
                    Totale aggregato ({appliedRows.length}{" "}
                    {appliedRows.length === 1 ? "struttura" : "strutture"})
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-emerald-800">{aggregates.occPerc}%</span>
                  </td>
                  <td className="px-4 py-3 text-emerald-900 whitespace-nowrap">
                    {aggregates.occ} / {aggregates.postiT}
                  </td>
                  <td className="px-4 py-3 text-emerald-900 whitespace-nowrap">
                    <span className="tabular-nums">{aggregates.ospitiResidenti}</span>
                    <span className="text-[11px] text-emerald-700/80 ml-1">
                      / {aggregates.ospitiAttivi}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-emerald-900">{formatEuro(aggregates.ricavi)}</td>
                  <td className="px-4 py-3 text-emerald-900">{formatEuro(aggregates.spese)}</td>
                  <td className="px-5 py-3 text-emerald-900">{formatEuro(aggregates.margine)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Deblistering + Diario consegne (oggi); sotto: Azioni rapide | Attività recenti */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
        <Card className="card-base bg-white border border-slate-200/70 shadow-sm overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-start gap-2 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-50 ring-1 ring-cyan-100">
                  <Package className="h-5 w-5 text-cyan-600" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-slate-900 leading-tight">Deblistering</h3>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Confronto sulle strutture incluse nei totali ({appliedRows.length}{" "}
                    {appliedRows.length === 1 ? "selezionata" : "selezionate"})
                  </p>
                </div>
              </div>
              <Link
                to="/deblistering"
                className="text-[11px] font-semibold text-cyan-700 hover:text-cyan-800 whitespace-nowrap shrink-0 inline-flex items-center gap-0.5"
              >
                Modulo
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-100">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2">Struttura</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">Aderenti</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">Su posti</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">Pendenti</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">Ricavo stim.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {deblisteringApplied.rows.map((row) => {
                    const att = Number(row.deb.ospiti_attivi) || 0;
                    const pend =
                      (Number(row.deb.fatture_da_assegnare) || 0) +
                      (Number(row.deb.fatture_da_inserire) || 0);
                    const ric = Number(row.deb.ricavo_mensile_stimato) || 0;
                    const percPosti =
                      row.posti_totali > 0 ? Math.round((att / row.posti_totali) * 100) : null;
                    return (
                      <tr key={row.id}>
                        <td className="px-3 py-2 font-medium text-slate-800 max-w-[140px] truncate" title={row.name}>
                          {row.name}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700">{att}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                          {percPosti !== null ? `${percPosti}%` : "—"}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {pend > 0 ? (
                            <span className="font-semibold text-amber-700">{pend}</span>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700 whitespace-nowrap">
                          €{" "}
                          {ric.toLocaleString("it-IT", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    );
                  })}
                  {deblisteringApplied.rows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-slate-400">
                        Nessuna struttura nei totali
                      </td>
                    </tr>
                  ) : (
                    <tr className="bg-slate-50/90 font-semibold text-slate-800">
                      <td className="px-3 py-2">Totale selezione</td>
                      <td className="px-3 py-2 text-right tabular-nums">{deblisteringApplied.totAtt}</td>
                      <td className="px-3 py-2 text-right text-slate-400">—</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {deblisteringApplied.totPend > 0 ? (
                          <span className="text-amber-800">{deblisteringApplied.totPend}</span>
                        ) : (
                          "0"
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                        €{" "}
                        {deblisteringApplied.totRicavo.toLocaleString("it-IT", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-slate-400 mt-3 leading-snug">
              Pendenti = fatture farmacia da assegnare o da inserire in fattura (per tenant della struttura). Percentuale
              aderenti su posti letto della struttura.
            </p>
          </CardContent>
        </Card>

        <Card className="card-base bg-white border border-slate-200/70 shadow-sm overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-start gap-2 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 ring-1 ring-blue-100">
                  <BookOpen className="h-5 w-5 text-blue-600" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-slate-900 leading-tight">Diario consegne</h3>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Oggi sulle strutture incluse nei totali ({appliedRows.length}{" "}
                    {appliedRows.length === 1 ? "selezionata" : "selezionate"})
                  </p>
                </div>
              </div>
              <Link
                to="/diario-consegne"
                className="text-[11px] font-semibold text-blue-700 hover:text-blue-800 whitespace-nowrap shrink-0 inline-flex items-center gap-0.5"
              >
                Modulo
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-4">
                <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Consegne</p>
                <p className="mt-1 text-3xl font-bold tabular-nums text-slate-900">{diarioOggiAggregato.consegne}</p>
                <p className="text-[11px] text-slate-400 mt-1">registrate oggi</p>
              </div>
              <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-4">
                <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Segnalazioni</p>
                <p className="mt-1 text-3xl font-bold tabular-nums text-slate-900">{diarioOggiAggregato.segnalazioni}</p>
                <p className="text-[11px] text-slate-400 mt-1">nelle consegne di oggi</p>
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-4 leading-snug">
              Conteggi aggregati sul turno calendario di oggi (fuso Europe/Rome). Senza permesso di lettura del diario i
              valori possono risultare zero.
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
        <Card className="card-base bg-white border border-slate-200/70 shadow-sm">
          <CardContent className="p-5">
            <h3 className="text-base font-bold text-slate-900 mb-4">Azioni rapide</h3>
            <div className="grid grid-cols-3 sm:grid-cols-3 gap-2.5">
              {azioniRapide.map((a, idx) => (
                <Link
                  key={idx}
                  to={a.to}
                  className="flex flex-col items-center justify-center gap-1.5 py-3 px-2 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-all text-center"
                >
                  <div className={`w-10 h-10 rounded-xl ${a.bg} ring-1 ${a.ring} flex items-center justify-center`}>
                    <a.icon className={`w-5 h-5 ${a.color}`} />
                  </div>
                  <span className="text-[11px] font-semibold text-slate-700 leading-tight">{a.label}</span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="card-base bg-white border border-slate-200/70 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Clock className="w-5 h-5 text-slate-600 shrink-0" strokeWidth={2} />
                <h3 className="text-base font-bold text-slate-900 truncate">Attività recenti</h3>
              </div>
              <span className="text-[11px] text-slate-400 whitespace-nowrap shrink-0">
                Filtrate sulla selezione
              </span>
            </div>

            {activitiesFiltered.length > 0 ? (
              <div className="space-y-0.5 max-h-[280px] overflow-y-auto pr-1 -mr-1">
                {activitiesFiltered.map((att, idx) => {
                  const Icon = att.tipo === "fattura" ? Receipt : FileText;
                  const iconBg = att.tipo === "fattura" ? "bg-purple-50" : "bg-orange-50";
                  const iconColor = att.tipo === "fattura" ? "text-purple-600" : "text-orange-600";
                  const href =
                    att.tipo === "fattura" && !dashboardFatturazione ? "/prima-nota-ospiti" : att.to;
                  const when = formatActivityWhen(att.occurred_at);
                  return (
                    <Link
                      key={`${att.tipo}-${att.struttura_id}-${idx}`}
                      to={href}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors group border border-transparent hover:border-slate-100/80"
                    >
                      <div
                        className={`w-8 h-8 rounded-lg ${iconBg} ring-1 ring-slate-100/80 flex items-center justify-center shrink-0`}
                      >
                        <Icon className={`w-4 h-4 ${iconColor}`} strokeWidth={2.2} />
                      </div>
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <div className="flex items-baseline justify-between gap-2 min-w-0">
                          <p className="text-[13px] font-semibold text-slate-800 leading-tight truncate">{att.titolo}</p>
                          <p
                            className="text-[10px] font-medium text-cyan-800 truncate text-right shrink-0 max-w-[45%]"
                            title={att.struttura_name}
                          >
                            {att.struttura_name}
                          </p>
                        </div>
                        <div className="flex items-baseline justify-between gap-2 min-w-0">
                          <p className="text-[11px] text-slate-500 leading-tight truncate">{att.sottotitolo}</p>
                          {when ? (
                            <time
                              dateTime={att.occurred_at}
                              className="text-[10px] text-slate-400 tabular-nums whitespace-nowrap shrink-0"
                            >
                              {when}
                            </time>
                          ) : (
                            <span className="text-[10px] text-slate-300 shrink-0">—</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 shrink-0 self-center" />
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="py-10 text-center text-sm text-slate-400">
                Nessuna attività recente per le strutture selezionate
              </div>
            )}
            <div className="mt-4 pt-3 border-t border-slate-100">
              <Link
                to="/fascicoli-assistenziali"
                className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1"
              >
                Apri fascicoli assistenziali
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
